// react
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import type { Dispatch, SetStateAction } from 'react';

// feature-hooks
import useChartOptions from '@/features/data-viewer/hooks/useChartOptions';

// components
import useHighchartsThemeSync from '@/hooks/useHighchartsThemeSync';

// relative
import type { ChartSeries, DateRange, SeriesVisibility, YAxis } from '../types';

import Highcharts, { merge } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import 'highcharts/modules/boost';
import 'highcharts/modules/stock';
import type { DateTime } from 'luxon';

export interface DataViewerChartHandle {
  resetExtremes: (from: DateTime, to: DateTime) => void;
  /** Highlight one series (dim all others). Pass null to reset all states. */
  highlightSeries: (seriesId: string | null) => void;
}

interface DataViewerChartProps {
  isLoading: boolean;
  chartSeries: ChartSeries[];
  yAxes: YAxis[];
  seriesVisibility: SeriesVisibility;
  setSeriesVisibility: Dispatch<SetStateAction<SeriesVisibility>>;
  onAfterSetExtremes?: (min: number, max: number) => void;
  navigatorSeries?: ChartSeries[];
  dateRange: DateRange;
  selectedDateRange: DateRange;
  liveMode: boolean;
  rawDataMode: boolean;
  yAxisStartsAtZero?: boolean;
}

const DataViewerChart = forwardRef<DataViewerChartHandle, DataViewerChartProps>(
  (
    {
      isLoading,
      chartSeries,
      yAxes,
      seriesVisibility,
      setSeriesVisibility,
      onAfterSetExtremes,
      navigatorSeries,
      dateRange,
      selectedDateRange,
      liveMode,
      rawDataMode,
      yAxisStartsAtZero,
    },
    ref,
  ) => {
    const chartRef = useRef<HighchartsReact.RefObject>(null);

    useImperativeHandle(ref, () => ({
      resetExtremes: (from: DateTime, to: DateTime) => {
        const chart = chartRef.current?.chart;
        if (chart?.xAxis?.[0]) {
          chart.xAxis[0].setExtremes(
            from.toMillis(),
            to.toMillis(),
            true,
            false,
          );
        }
      },
      highlightSeries: (seriesId: string | null) => {
        const chart = chartRef.current?.chart;
        if (!chart) return;
        chart.series.forEach((s) => {
          if (seriesId === null) {
            s.setState('');
          } else if (s.options.id === seriesId) {
            s.setState('');
          } else {
            s.setState('inactive');
          }
        });
      },
    }));

    const initialTheme = useHighchartsThemeSync(
      chartRef,
      isLoading && !liveMode,
    );

    const chartOptions = useChartOptions({
      chartSeries,
      liveMode,
      rawDataMode,
      yAxisStartsAtZero,
      yAxes,
      seriesVisibility,
      setSeriesVisibility,
      dateRange,
      selectedDateRange,
      onAfterSetExtremes: liveMode ? onAfterSetExtremes : onAfterSetExtremes,
    });

    // Merge theme with chart options, ensuring each yAxis gets both theme styling and specific config
    const chartOptionsWithTheme = merge(initialTheme, {
      ...chartOptions,
      accessibility: {
        enabled: false,
      },
      yAxis: chartOptions.yAxis.map((axis) =>
        merge(initialTheme.yAxis[0], axis),
      ),
    });

    // Update navigator series imperatively so it never interferes with the
    // main chart options update (which would cause layout shifts and flicker).
    useEffect(() => {
      const chart = chartRef.current?.chart;
      if (!chart || !navigatorSeries || navigatorSeries.length === 0) return;
      const data = navigatorSeries[0]?.data ?? [];
      if (data.length === 0) return;
      chart.update({
        navigator: {
          series: { data },
          xAxis: {
            min: selectedDateRange.from.toMillis(),
            max: selectedDateRange.to.toMillis(),
          },
        },
      });
    }, [navigatorSeries, liveMode, selectedDateRange]);

    return (
      <HighchartsReact
        containerProps={{ style: { height: '600px', width: '100%' } }}
        highcharts={Highcharts}
        options={chartOptionsWithTheme}
        ref={chartRef}
      />
    );
  },
);

DataViewerChart.displayName = 'DataViewerChart';

export default DataViewerChart;
